import { FileInfoDto, FileType, QueryFactory } from '@lmt/shared-types';

import { IconButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/IconButton';
import { DocumentVersionPopup } from '@lmt/unicorn-shared/app/components/documents/DocumentVersionPopup';
import {
  getDocumentExtension,
  getDocumentTitle,
} from '@lmt/unicorn-shared/app/components/patients/profile/cases/components/documents/common';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import EditIcon from '@lmt/unicorn-shared/src/app/icons/edit.svg?react';
import HistoryIcon from '@lmt/unicorn-shared/src/app/icons/version-history.svg?react';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';
import { parseError } from '../../helpers/error-handling';

import styles from './DocumentViewerHeader.module.scss';

type DocumentViewerHeaderProps = {
  documentId: string;
  onVersionChange: (selectedDocumentId: string) => void;
};

const DocumentViewerHeader: React.FC<DocumentViewerHeaderProps> = React.memo(
  function DocumentViewerHeader(props) {
    const { documentId, onVersionChange } = props;

    const { data: document } =
      QueryFactory.FileQuery.useGetFileInfoQuery(documentId);

    // const versionHistory = QueryFactory.FileQuery.useGetVersionHistoryQuery(
    //   document?.groupId!,
    //   { enabled: !!document?.groupId },
    // );

    const i18n = useScopedTranslation('patients.documents.versions');
    const i18next = useScopedTranslation('patients.documents');

    const dispatch = useUniDispatch();
    const modals = useModal();

    const queryClient = useQueryClient();

    const invalidateDocumentQueries = useCallback(async () => {
      await queryClient.invalidateQueries({
        queryKey: QueryFactory.FileQuery.getFileInfoQueryKey(documentId),
      });
      if (document) {
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.FileQuery.getVersionHistoryQueryKey(
            document.groupId,
          ),
        });
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.FileQuery.searchFilesQueryKey({
            dataBlockId: document.dataBlockId,
          }),
        });
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.FileQuery.searchFilesQueryKey({
            patientId: document.patientId,
          }),
        });
      }
    }, [documentId, document]);

    // const version =
    //   (versionHistory.data?.findIndex((f) => f.id == documentId) ?? -1) + 1;
    const isDraft = document?.type === FileType.DocumentDraft;

    const documentName = document?.name.substring(
      0,
      document.name.lastIndexOf('.'),
    );

    const renameDocument = useCallback(async () => {
      try {
        if (document) {
          if (
            document.type === FileType.Document ||
            (document.type === FileType.DocumentDraft &&
              document.versionNumber !== 1)
          ) {
            return await modals.showAlert({
              title: i18next.t('rename_confirmation_title'),
              text: i18next.t('rename_confirmation_text'),
            });
          }
          const newName = await modals.showPrompt({
            defaultValue: getDocumentTitle(document?.name),
            text: i18next.t('rename_prompt_text'),
            title: i18next.t('rename_prompt_title'),
            maxLength: 200,
            validate: (value: string) => !/[\/\\]/.test(value),
            errorText: i18next.t('file_name_invalid_characters'),
          });

          const ext = getDocumentExtension(document?.name);
          if (newName) {
            const newDocument = await QueryFactory.FileClient.patchFile(
              documentId,
              { name: newName + ext },
            );
            onVersionChange(newDocument.id);
          }
          await invalidateDocumentQueries();
        }
      } catch (error) {
        await modals.showError({ text: parseError(error) });
      }
    }, [dispatch, queryClient, document?.name]);

    const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);

    // Ignore clicks on already selected version
    const onVersionClick = useCallback(
      (file: FileInfoDto) => {
        file.type == FileType.DocumentDraft &&
          file.id != documentId &&
          onVersionChange(file.id);

        return file.type === FileType.DocumentDraft;
      },
      [documentId, onVersionChange],
    );

    return (
      <div className={styles.container}>
        {documentName && (
          <>
            <div className={styles.documentName} data-test-id="document-name">
              {documentName}
            </div>
            <IconButton
              iconComponent={<EditIcon className={styles.icon} />}
              onClick={renameDocument}
              disabled={!isDraft}
              testId="rename-document-button"
            />
          </>
        )}
        {document && (
          <>
            <div className={styles.version} data-test-id="document-type">
              {i18n.t('draft')}
              {/* {isDraft
                ? i18n.t('draft')
                : i18n.t('version', { versionNumber: version })} */}
            </div>

            <IconButton
              iconComponent={<HistoryIcon className={styles.icon} />}
              onClick={() => setIsPopupOpen(!isPopupOpen)}
              testId="document-version-history-button"
            />
            <DocumentVersionPopup
              isOpen={isPopupOpen}
              onClose={() => setIsPopupOpen(false)}
              groupId={document?.groupId}
              highlightCurrentVersion={false}
              onVersionClick={onVersionClick}
            />
          </>
        )}
      </div>
    );
  },
);
export default DocumentViewerHeader;
